/**
 * PortfolioPage.jsx — Portfolio Tracking Hub
 *
 * Tabs: Overview | Holdings | Transactions | News & Events | Broker Connections
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import headerLogo from '../assets/branding/Header_logo.png';
import HeaderBackButton from '../components/HeaderBackButton';
import {
    getPortfolioOverview,
    getPortfolioXIRR,
    getAllocation,
    getAssetClassAllocation,
    getRiskSummary,
    getDrift,
    getConcentrationWarnings,
    getHoldings,
    getHoldingDetail,
    getHoldingXIRR,
    getHoldingNews,
    getTransactions,
    getPortfolioNews,
    markNewsRead,
    getEarningsCalendar,
    getDividendEvents,
    listBrokerConnections,
    connectBroker,
    disconnectBroker,
    refreshBrokerSession,
    triggerSync,
    getSyncStatus,
    importTransactionsCSV,
} from '../services/portfolioService';
import SectorAllocationCard from '../components/portfolio/SectorAllocationCard';
import SectorDetailsModal from '../components/portfolio/SectorDetailsModal';
import AssetAllocationCard from '../components/portfolio/AssetAllocationCard';
import DirectVsFundsCard from '../components/portfolio/DirectVsFundsCard';
import AllocationTargetsModal from '../components/portfolio/AllocationTargetsModal';
import GoldHoldingsTab from '../components/portfolio/GoldHoldingsTab';
import MutualFundsTab from '../components/portfolio/MutualFundsTab';
import '../styles/PortfolioPage.css';

/* ───────── helpers ───────── */
const fmt = (n, decimals = 2) => {
    if (n == null || isNaN(n)) return 'N/A';
    return Number(n).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};
const fmtCurrency = (n) => (n == null || isNaN(n) ? 'N/A' : `₹${fmt(n)}`);
const fmtPct = (n) => (n == null || isNaN(n) ? 'N/A' : `${fmt(n)}%`);
const pnlClass = (n) => (n > 0 ? 'pnl-positive' : n < 0 ? 'pnl-negative' : '');
const pnlKpiClass = (n) => (n > 0 ? 'portfolio-kpi-value--positive' : n < 0 ? 'portfolio-kpi-value--negative' : '');
const relativeTime = (d) => {
    if (!d) return '';
    const ms = Date.now() - new Date(d).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
};

const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'holdings', label: 'Holdings' },
    { key: 'gold', label: 'Gold / Commodities' },
    { key: 'mutualfunds', label: 'Mutual Funds' },
    { key: 'transactions', label: 'Transactions' },
    { key: 'news', label: 'News & Events' },
    { key: 'brokers', label: 'Broker Connections' },
];

const PortfolioPage = () => {
    const navigate = useNavigate();
    const context = useOutletContext() || {};
    const isDarkMode = context.isDarkMode ?? localStorage.getItem('theme') === 'dark';
    const toggleTheme = context.toggleTheme ?? (() => { });
    const handleMenuOpen = context.handleMenuOpen ?? (() => { });
    const getUserInitials = context.getUserInitials ?? (() => 'U');
    const getDisplayName = context.getDisplayName ?? (() => 'User');
    const getProfilePhotoUrl = context.getProfilePhotoUrl ?? (() => '');
    const getProfilePhotoLoading = context.getProfilePhotoLoading ?? (() => false);
    const profilePhotoUrl = getProfilePhotoUrl();
    const profilePhotoLoading = getProfilePhotoLoading();

    const [searchParams, setSearchParams] = useSearchParams();
    const activeTabParam = searchParams.get('tab') || 'overview';
    const [activeTab, setActiveTab] = useState(activeTabParam);

    const changeTab = (key) => {
        setActiveTab(key);
        setSearchParams({ tab: key }, { replace: true });
    };

    return (
        <div className="portfolio-page">
            <main className="portfolio-main">
                <header className="portfolio-header">
                    <div className="portfolio-header-lead">
                    <div className="header-brand" onClick={() => navigate('/home')}>
                        <img src={headerLogo} alt="Finlytics Forge" className="header-logo-svg" />
                        <span className="brand__text">
                            <span className="brand__title">Finlytics</span>
                            <span className="brand__subtitle">Forge</span>
                        </span>
                    </div>
                    <HeaderBackButton to="/home" label="Home" />
                    </div>

                    <div className="header-tagline">Live Portfolio Tracking</div>

                    <div className="header-actions">
                        <button className="action-btn" onClick={toggleTheme} title={isDarkMode ? 'Light Mode' : 'Dark Mode'}>
                            {isDarkMode ? '☀️' : '🌙'}
                        </button>
                        <div className="user-profile" onClick={handleMenuOpen}>
                            <span className="user-name">{getDisplayName()}</span>
                            <div className="user-avatar">
                                {profilePhotoLoading ? (
                                    <div className="user-avatar-shimmer" />
                                ) : profilePhotoUrl ? (
                                    <img src={profilePhotoUrl} alt={getDisplayName()} className="user-avatar-img" />
                                ) : (
                                    getUserInitials()
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* ── Tab bar ── */}
                <nav className="portfolio-tabs" aria-label="Portfolio tabs">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            className={`portfolio-tab ${activeTab === t.key ? 'portfolio-tab--active' : ''}`}
                            onClick={() => changeTab(t.key)}
                            aria-selected={activeTab === t.key}
                            role="tab"
                        >
                            {t.label}
                        </button>
                    ))}
                </nav>

                {/* ── Tab content ── */}
                {activeTab === 'overview' && <OverviewTab />}
                {activeTab === 'holdings' && <HoldingsTab />}
                {activeTab === 'gold' && <GoldHoldingsTab />}
                {activeTab === 'mutualfunds' && <MutualFundsTab />}
                {activeTab === 'transactions' && <TransactionsTab />}
                {activeTab === 'news' && <NewsTab />}
                {activeTab === 'brokers' && <BrokersTab />}
            </main>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   TAB 1: OVERVIEW
   ═══════════════════════════════════════════════════════════════ */
const OverviewTab = () => {
    const [overview, setOverview] = useState(null);
    const [xirr, setXirr] = useState(null);
    const [allocation, setAllocation] = useState(null);
    const [assetAlloc, setAssetAlloc] = useState(null);
    const [risk, setRisk] = useState(null);
    const [drift, setDrift] = useState(null);
    const [concentration, setConcentration] = useState(null);
    const [syncStatus, setSyncStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedSector, setSelectedSector] = useState(null);
    const [showTargetsModal, setShowTargetsModal] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [ov, xi, al, ac, ri, dr, cn, ss] = await Promise.allSettled([
                getPortfolioOverview(),
                getPortfolioXIRR(),
                getAllocation(),
                getAssetClassAllocation(),
                getRiskSummary(),
                getDrift(),
                getConcentrationWarnings(),
                getSyncStatus(),
            ]);
            if (ov.status === 'fulfilled') setOverview(ov.value?.data ?? ov.value);
            if (xi.status === 'fulfilled') setXirr(xi.value?.data ?? xi.value);
            if (al.status === 'fulfilled') setAllocation(al.value?.data ?? al.value);
            if (ac.status === 'fulfilled') setAssetAlloc(ac.value?.data ?? ac.value);
            if (ri.status === 'fulfilled') setRisk(ri.value?.data ?? ri.value);
            if (dr.status === 'fulfilled') setDrift(dr.value?.data ?? dr.value);
            if (cn.status === 'fulfilled') setConcentration(cn.value?.data ?? cn.value);
            if (ss.status === 'fulfilled') setSyncStatus(ss.value?.data ?? ss.value);
        } catch { /* ignore */ }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            await triggerSync();
            // Re-poll after a short delay
            setTimeout(load, 3000);
        } catch { /* ignore */ }
        setSyncing(false);
    };

    if (loading) return <div className="portfolio-loading">Loading portfolio…</div>;

    if (!overview) {
        return (
            <div className="portfolio-empty">
                <div className="portfolio-empty-icon">📊</div>
                <div className="portfolio-empty-title">No portfolio data yet</div>
                <p>Connect a broker to start tracking your portfolio.</p>
            </div>
        );
    }

    const sectorData = allocation?.sector ?? [];

    return (
        <>
            {/* Sync bar */}
            <div className="portfolio-sync-bar">
                <span className={`sync-status-dot sync-status-dot--${syncStatus?.overallHealth ?? 'disconnected'}`} />
                <span className="sync-label">
                    {syncStatus?.lastSyncAt ? `Last synced ${relativeTime(syncStatus.lastSyncAt)}` : 'Not synced yet'}
                </span>
                {syncStatus?.brokers?.map((b) => (
                    <span key={b.broker} className="sync-label" style={{ opacity: 0.7 }}>
                        {b.broker}: <span className={`sync-status-dot sync-status-dot--${b.health}`} style={{ display: 'inline-block', verticalAlign: 'middle' }} />
                    </span>
                ))}
                <button className="sync-btn" onClick={handleSync} disabled={syncing}>
                    {syncing ? 'Syncing…' : '⟳ Sync now'}
                </button>
            </div>

            {/* KPIs */}
            <div className="portfolio-kpi-row">
                <div className="portfolio-kpi">
                    <div className="portfolio-kpi-label">Portfolio Value</div>
                    <div className="portfolio-kpi-value">{fmtCurrency(overview.currentValue)}</div>
                </div>
                <div className="portfolio-kpi">
                    <div className="portfolio-kpi-label">Day P&L</div>
                    <div className={`portfolio-kpi-value ${pnlKpiClass(overview.dayPnl)}`}>{fmtCurrency(overview.dayPnl)}</div>
                    <div className="portfolio-kpi-sub">{fmtPct(overview.dayPnlPct)}</div>
                </div>
                <div className="portfolio-kpi">
                    <div className="portfolio-kpi-label">Total P&L</div>
                    <div className={`portfolio-kpi-value ${pnlKpiClass(overview.totalPnl)}`}>{fmtCurrency(overview.totalPnl)}</div>
                    <div className="portfolio-kpi-sub">{fmtPct(overview.totalPnlPct)}</div>
                </div>
                <div className="portfolio-kpi">
                    <div className="portfolio-kpi-label">Portfolio XIRR</div>
                    <div className={`portfolio-kpi-value ${pnlKpiClass(xirr?.portfolioXIRR)}`}>{fmtPct(xirr?.portfolioXIRR)}</div>
                    <div className="portfolio-kpi-sub">
                        {xirr?.confidence && (
                            <span className={`confidence-badge confidence-badge--${xirr.confidence}`}>
                                {xirr.confidence} confidence
                            </span>
                        )}
                        {xirr?.label && <span style={{ marginLeft: 4 }}>{xirr.label}</span>}
                    </div>
                </div>
            </div>

            {/* Sector Allocation (pie chart with drill-down) */}
            <SectorAllocationCard
                sectorData={sectorData}
                onSectorClick={(sector) => setSelectedSector(sector)}
            />

            {/* Sector Details Modal */}
            {selectedSector && (
                <SectorDetailsModal
                    sector={selectedSector}
                    onClose={() => setSelectedSector(null)}
                />
            )}

            {/* Asset-Class Allocation & Direct vs Funds charts (side by side) */}
            {assetAlloc && (
                <div className="portfolio-charts-row">
                    <AssetAllocationCard data={assetAlloc} />
                    <DirectVsFundsCard data={assetAlloc} />
                </div>
            )}

            {/* Drift from allocation targets */}
            {drift && drift.categories?.length > 0 ? (
                <section className="portfolio-section">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 className="portfolio-section-title" style={{ margin: 0 }}>Allocation Drift</h3>
                        <button className="broker-action-btn broker-action-btn--secondary" onClick={() => setShowTargetsModal(true)} style={{ fontSize: '0.78rem' }}>
                            ⚙ Edit Targets
                        </button>
                    </div>
                    <p className="sector-subtitle">Difference between your actual allocation and targets (threshold: {fmtPct(drift.driftThreshold)})</p>
                    <div className="holdings-table-wrap">
                        <table className="holdings-table">
                            <thead>
                                <tr>
                                    <th>Category</th>
                                    <th className="text-right">Target %</th>
                                    <th className="text-right">Actual %</th>
                                    <th className="text-right">Drift</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {drift.categories.map((c) => {
                                    const driftAbs = Math.abs(c.driftPct);
                                    const overThreshold = driftAbs > (drift.driftThreshold || 5);
                                    return (
                                        <tr key={c.category}>
                                            <td style={{ fontWeight: 600 }}>{c.category}</td>
                                            <td className="text-right">{fmtPct(c.targetPct)}</td>
                                            <td className="text-right">{fmtPct(c.actualPct)}</td>
                                            <td className={`text-right ${c.driftPct > 0 ? 'pnl-positive' : c.driftPct < 0 ? 'pnl-negative' : ''}`}>
                                                {c.driftPct > 0 ? '+' : ''}{fmtPct(c.driftPct)}
                                            </td>
                                            <td>
                                                {overThreshold
                                                    ? <span className="drift-badge drift-badge--warning">⚠ Drifted</span>
                                                    : <span className="drift-badge drift-badge--ok">✓ OK</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {drift.suggestions?.length > 0 && (
                        <div className="drift-suggestions">
                            <strong>Suggestions:</strong>
                            <ul>
                                {drift.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                        </div>
                    )}
                </section>
            ) : (
                <section className="portfolio-section" style={{ textAlign: 'center', padding: '1.5rem' }}>
                    <p style={{ color: 'var(--text-2)', fontSize: '0.88rem', marginBottom: '0.75rem' }}>
                        Set allocation targets to enable drift monitoring and rebalancing suggestions.
                    </p>
                    <button className="broker-action-btn broker-action-btn--primary" onClick={() => setShowTargetsModal(true)}>
                        Set Allocation Targets
                    </button>
                </section>
            )}

            {/* Allocation Targets Modal */}
            {showTargetsModal && (
                <AllocationTargetsModal
                    onClose={() => setShowTargetsModal(false)}
                    onSaved={load}
                />
            )}

            {/* Concentration Warnings */}
            {concentration && concentration.warnings?.length > 0 && (
                <section className="portfolio-section">
                    <h3 className="portfolio-section-title">Concentration Warnings</h3>
                    <div className="concentration-warnings">
                        {concentration.warnings.map((w, i) => (
                            <div key={i} className="concentration-warning-item">
                                <span className="concentration-warning-icon">⚠️</span>
                                <span>{w}</span>
                            </div>
                        ))}
                    </div>
                    {concentration.top5 && (
                        <div className="holdings-table-wrap" style={{ marginTop: '0.75rem' }}>
                            <table className="holdings-table">
                                <thead>
                                    <tr>
                                        <th>Holding</th>
                                        <th className="text-right">Market Value</th>
                                        <th className="text-right">Global Weight</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {concentration.top5.map((h) => (
                                        <tr key={h.instrumentId || h.name}>
                                            <td style={{ fontWeight: 600 }}>{h.name}</td>
                                            <td className="text-right">{fmtCurrency(h.marketValue)}</td>
                                            <td className="text-right">{fmtPct(h.globalWeightPct)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            {/* Risk Summary */}
            {risk && (
                <section className="portfolio-section">
                    <h3 className="portfolio-section-title">Risk Summary</h3>
                    <div className="portfolio-kpi-row">
                        <div className="portfolio-kpi">
                            <div className="portfolio-kpi-label">Max Drawdown</div>
                            <div className="portfolio-kpi-value portfolio-kpi-value--negative">{fmtPct(risk.maxDrawdown)}</div>
                        </div>
                        <div className="portfolio-kpi">
                            <div className="portfolio-kpi-label">Volatility (ann.)</div>
                            <div className="portfolio-kpi-value">{fmtPct(risk.volatility)}</div>
                        </div>
                        <div className="portfolio-kpi">
                            <div className="portfolio-kpi-label">Top 5 Concentration</div>
                            <div className="portfolio-kpi-value">{fmtPct(risk.top5Concentration)}</div>
                        </div>
                    </div>
                </section>
            )}

            {/* Rebalancing Suggestions — shown inline if drift has suggestions */}
            {drift && drift.suggestions?.length > 0 && (
                <section className="portfolio-section">
                    <h3 className="portfolio-section-title">Rebalancing Suggestions</h3>
                    <p className="sector-subtitle">Cashflow-first approach: prioritise adding to under-weight categories before reducing others</p>
                    <div className="rebalancing-actions">
                        {drift.suggestions.map((s, i) => {
                            const isAdd = s.toLowerCase().includes('add') || s.toLowerCase().includes('increase');
                            return (
                                <div key={i} className={`rebalancing-action-card rebalancing-action-card--${isAdd ? 'ADD' : 'REDUCE'}`}>
                                    <div className="rebalancing-action-label">{isAdd ? '+ Add' : '− Reduce'}</div>
                                    <div className="rebalancing-action-detail">{s}</div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </>
    );
};

/* ═══════════════════════════════════════════════════════════════
   TAB 2: HOLDINGS
   ═══════════════════════════════════════════════════════════════ */
const HoldingsTab = () => {
    const [holdings, setHoldings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedHolding, setSelectedHolding] = useState(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const res = await getHoldings('EQUITY');
                setHoldings(res?.data ?? res ?? []);
            } catch { setHoldings([]); }
            setLoading(false);
        })();
    }, []);

    if (loading) return <div className="portfolio-loading">Loading holdings…</div>;

    if (!holdings.length) {
        return (
            <div className="portfolio-empty">
                <div className="portfolio-empty-icon">📋</div>
                <div className="portfolio-empty-title">No holdings</div>
                <p>Connect a broker and sync to see your holdings.</p>
            </div>
        );
    }

    return (
        <>
            <section className="portfolio-section portfolio-section--full">
                <h3 className="portfolio-section-title">Unified Holdings</h3>
                <div className="holdings-table-wrap">
                    <table className="holdings-table">
                        <thead>
                            <tr>
                                <th>Stock Symbol</th>
                                <th className="text-right">Total Qty</th>
                                <th className="text-right">Qty Held &gt; 1 Yr</th>
                                <th className="text-right">Avg Cost</th>
                                <th className="text-right">Last Price</th>
                                <th className="text-right">Day Change</th>
                                <th className="text-right">Value At Cost</th>
                                <th className="text-right">Market Value</th>
                                <th className="text-right">Absolute P&L</th>
                                <th className="text-right">Profit/Loss (%)</th>
                                <th className="text-right">Weight</th>
                                <th className="text-right">XIRR</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {holdings.map((h) => {
                                const valueAtCost = h.valueAtCost ?? ((h.quantity || 0) * (h.avgCost || 0));
                                const absolutePnl = h.unrealizedPnl ?? (
                                    (h.marketValue != null && valueAtCost != null) ? (h.marketValue - valueAtCost) : null
                                );
                                const profitLossPct = valueAtCost ? (absolutePnl / valueAtCost) * 100 : null;

                                return (
                                    <tr key={h.instrumentId}>
                                        <td style={{ fontWeight: 600 }}>{h.name || h.instrumentId}</td>
                                        <td className="text-right">{fmt(h.quantity, 0)}</td>
                                        <td className="text-right">{h.qtyHeldOver1Y != null ? fmt(h.qtyHeldOver1Y, 0) : '—'}</td>
                                        <td className="text-right">{fmtCurrency(h.avgCost)}</td>
                                        <td className="text-right">{fmtCurrency(h.lastPrice)}</td>
                                        <td className={`text-right ${pnlClass(h.dayChange)}`}>
                                            {h.dayChange != null ? fmtCurrency(h.dayChange * (h.quantity || 1)) : 'N/A'}
                                            {h.dayChangePct != null && <span style={{ fontSize: '0.8em', opacity: 0.7 }}> ({fmtPct(h.dayChangePct)})</span>}
                                        </td>
                                        <td className="text-right">{fmtCurrency(valueAtCost)}</td>
                                        <td className="text-right">{fmtCurrency(h.marketValue)}</td>
                                        <td className={`text-right ${pnlClass(absolutePnl)}`}>{fmtCurrency(absolutePnl)}</td>
                                        <td className={`text-right ${pnlClass(profitLossPct)}`}>{fmtPct(profitLossPct)}</td>
                                        <td className="text-right">{fmtPct(h.globalWeightPct ?? h.weight)}</td>
                                        <td className={`text-right ${pnlClass(h.xirr)}`}>{h.xirr != null ? fmtPct(h.xirr) : 'N/A'}</td>
                                        <td>
                                            <button
                                                className="broker-action-btn broker-action-btn--secondary"
                                                onClick={() => setSelectedHolding(h)}
                                            >
                                                Details
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Holding detail drawer */}
            {selectedHolding && (
                <HoldingDetailDrawer
                    holding={selectedHolding}
                    onClose={() => setSelectedHolding(null)}
                />
            )}
        </>
    );
};

/* ── Holding Detail Drawer ── */
const HoldingDetailDrawer = ({ holding, onClose }) => {
    const [detail, setDetail] = useState(null);
    const [holdingXIRR, setHoldingXIRR] = useState(null);
    const [news, setNews] = useState([]);
    const [loadingDetail, setLoadingDetail] = useState(true);

    const holdingValueAtCost = holding.valueAtCost ?? ((holding.quantity || 0) * (holding.avgCost || 0));
    const holdingAbsolutePnl = holding.unrealizedPnl ?? (
        (holding.marketValue != null && holdingValueAtCost != null) ? (holding.marketValue - holdingValueAtCost) : null
    );
    const holdingProfitLossPct = holdingValueAtCost ? (holdingAbsolutePnl / holdingValueAtCost) * 100 : null;

    useEffect(() => {
        (async () => {
            setLoadingDetail(true);
            try {
                const [d, x, n] = await Promise.allSettled([
                    getHoldingDetail(holding.instrumentId),
                    getHoldingXIRR(holding.instrumentId),
                    getHoldingNews(holding.instrumentId),
                ]);
                if (d.status === 'fulfilled') setDetail(d.value?.data ?? d.value);
                if (x.status === 'fulfilled') setHoldingXIRR(x.value?.data ?? x.value);
                if (n.status === 'fulfilled') setNews(n.value?.data ?? n.value ?? []);
            } catch { /* ignore */ }
            setLoadingDetail(false);
        })();
    }, [holding.instrumentId]);

    return (
        <div className="holding-drawer-overlay" onClick={onClose}>
            <div className="holding-drawer" onClick={(e) => e.stopPropagation()}>
                <button className="holding-drawer-close" onClick={onClose}>✕</button>
                <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.25rem' }}>{holding.name || holding.instrumentId}</h2>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-2)' }}>{holding.type} · {holding.instrumentId}</p>

                {loadingDetail ? (
                    <div className="portfolio-loading">Loading details…</div>
                ) : (
                    <>
                        {/* XIRR */}
                        <div className="portfolio-kpi-row" style={{ marginTop: '1.25rem' }}>
                            <div className="portfolio-kpi">
                                <div className="portfolio-kpi-label">Holding XIRR</div>
                                <div className={`portfolio-kpi-value ${pnlKpiClass(holdingXIRR?.xirr)}`}>
                                    {holdingXIRR?.xirr != null ? fmtPct(holdingXIRR.xirr) : 'N/A'}
                                </div>
                                {holdingXIRR?.confidence && (
                                    <span className={`confidence-badge confidence-badge--${holdingXIRR.confidence}`}>
                                        {holdingXIRR.confidence}
                                    </span>
                                )}
                            </div>
                            <div className="portfolio-kpi">
                                <div className="portfolio-kpi-label">Realized P&L</div>
                                <div className={`portfolio-kpi-value ${pnlKpiClass(detail?.realizedPnl)}`}>
                                    {fmtCurrency(detail?.realizedPnl)}
                                </div>
                            </div>
                            <div className="portfolio-kpi">
                                <div className="portfolio-kpi-label">Unrealized P&L</div>
                                <div className={`portfolio-kpi-value ${pnlKpiClass(holding.unrealizedPnl)}`}>
                                    {fmtCurrency(holding.unrealizedPnl)}
                                </div>
                            </div>
                            <div className="portfolio-kpi">
                                <div className="portfolio-kpi-label">Profit/Loss (%)</div>
                                <div className={`portfolio-kpi-value ${pnlKpiClass(holdingProfitLossPct)}`}>
                                    {fmtPct(holdingProfitLossPct)}
                                </div>
                            </div>
                        </div>

                        {/* Recent news for this holding */}
                        <section className="portfolio-section" style={{ marginTop: '1rem' }}>
                            <h3 className="portfolio-section-title">Recent News</h3>
                            {news.length === 0 ? (
                                <p style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>No recent news for this holding.</p>
                            ) : (
                                <div className="news-list">
                                    {news.slice(0, 5).map((n) => (
                                        <div key={n._id || n.url} className={`news-item ${n.isRead ? '' : 'news-item--unread'}`}>
                                            <div className="news-item-body">
                                                <div className="news-item-title">
                                                    <a href={n.url} target="_blank" rel="noopener noreferrer">{n.title}</a>
                                                </div>
                                                <div className="news-item-meta">{n.source} · {relativeTime(n.publishedAt)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
};

/* ═══════════════════════════════════════════════════════════════
   TAB 3: TRANSACTIONS
   ═══════════════════════════════════════════════════════════════ */
const TXN_TYPES = ['', 'BUY', 'SELL', 'DIVIDEND', 'INTEREST', 'FEE', 'TAX', 'DEPOSIT', 'WITHDRAWAL', 'MF_PURCHASE', 'MF_REDEMPTION'];

const TransactionsTab = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ broker: '', instrument: '', type: '', dateFrom: '', dateTo: '' });
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [incompleteHistory, setIncompleteHistory] = useState(false);
    const importRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getTransactions({ ...filters, page, limit: 50 });
            const data = res?.data ?? res;
            setTransactions(data?.transactions ?? data ?? []);
            setTotalPages(data?.totalPages ?? 1);
            setIncompleteHistory(data?.incompleteHistory ?? false);
        } catch { setTransactions([]); }
        setLoading(false);
    }, [filters, page]);

    useEffect(() => { load(); }, [load]);

    const handleFilterChange = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            await importTransactionsCSV(file, filters.broker || 'MANUAL');
            load();
        } catch { /* ignore */ }
        if (importRef.current) importRef.current.value = '';
    };

    return (
        <>
            {incompleteHistory && (
                <div className="txn-banner">
                    <span className="txn-banner-icon">⚠️</span>
                    <span>Transaction history may be incomplete for one or more brokers. Use Import CSV to backfill missing trades.</span>
                    <button className="txn-export-btn" onClick={() => importRef.current?.click()} style={{ marginLeft: 'auto' }}>
                        Import CSV
                    </button>
                    <input type="file" ref={importRef} accept=".csv" style={{ display: 'none' }} onChange={handleImport} />
                </div>
            )}

            <div className="txn-filters">
                <select className="txn-filter-select" value={filters.broker} onChange={(e) => handleFilterChange('broker', e.target.value)}>
                    <option value="">All Brokers</option>
                    <option value="ICICI_BREEZE">ICICI Direct</option>
                    <option value="GROWW">Groww</option>
                </select>
                <select className="txn-filter-select" value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}>
                    <option value="">All Types</option>
                    {TXN_TYPES.filter(Boolean).map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                </select>
                <input className="txn-filter-input" type="text" placeholder="Instrument" value={filters.instrument}
                    onChange={(e) => handleFilterChange('instrument', e.target.value)} />
                <input className="txn-filter-input" type="date" value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)} />
                <input className="txn-filter-input" type="date" value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)} />
                <button className="txn-export-btn" onClick={() => { /* TODO: trigger CSV export download */ }}>
                    Export CSV
                </button>
            </div>

            <section className="portfolio-section">
                {loading ? (
                    <div className="portfolio-loading">Loading transactions…</div>
                ) : transactions.length === 0 ? (
                    <div className="portfolio-empty">
                        <div className="portfolio-empty-icon">📒</div>
                        <div className="portfolio-empty-title">No transactions</div>
                        <p>Sync with a broker or import CSV to see your transaction ledger.</p>
                    </div>
                ) : (
                    <div className="holdings-table-wrap">
                        <table className="holdings-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Broker</th>
                                    <th>Type</th>
                                    <th>Instrument</th>
                                    <th>Qty</th>
                                    <th>Price</th>
                                    <th>Fees</th>
                                    <th>Taxes</th>
                                    <th>Net Cashflow</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transactions.map((t, i) => (
                                    <tr key={t.txnId || i}>
                                        <td>{new Date(t.dateTime).toLocaleDateString('en-IN')}</td>
                                        <td>{t.broker}</td>
                                        <td>{t.type}</td>
                                        <td>{t.instrumentName || t.instrumentId || '—'}</td>
                                        <td>{t.quantity != null ? fmt(t.quantity, 2) : '—'}</td>
                                        <td>{t.price != null ? fmtCurrency(t.price) : '—'}</td>
                                        <td>{t.fees != null ? fmtCurrency(t.fees) : '—'}</td>
                                        <td>{t.taxes != null ? fmtCurrency(t.taxes) : '—'}</td>
                                        <td className={pnlClass(t.netCashflow)}>{fmtCurrency(t.netCashflow)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                        <button className="txn-export-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-2)', alignSelf: 'center' }}>Page {page} / {totalPages}</span>
                        <button className="txn-export-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next →</button>
                    </div>
                )}
            </section>
        </>
    );
};

/* ═══════════════════════════════════════════════════════════════
   TAB 4: NEWS & EVENTS
   ═══════════════════════════════════════════════════════════════ */
const NewsTab = () => {
    const [news, setNews] = useState([]);
    const [earnings, setEarnings] = useState([]);
    const [dividends, setDividends] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [n, e, d] = await Promise.allSettled([
                    getPortfolioNews({ page: 1, limit: 30 }),
                    getEarningsCalendar(),
                    getDividendEvents(),
                ]);
                if (n.status === 'fulfilled') setNews(n.value?.data ?? n.value ?? []);
                if (e.status === 'fulfilled') setEarnings(e.value?.data ?? e.value ?? []);
                if (d.status === 'fulfilled') setDividends(d.value?.data ?? d.value ?? []);
            } catch { /* ignore */ }
            setLoading(false);
        })();
    }, []);

    const handleMarkRead = async (id) => {
        try {
            await markNewsRead(id);
            setNews((prev) => prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)));
        } catch { /* ignore */ }
    };

    if (loading) return <div className="portfolio-loading">Loading news…</div>;

    return (
        <>
            <section className="portfolio-section">
                <h3 className="portfolio-section-title">Daily Digest</h3>
                {news.length === 0 ? (
                    <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>No news yet. Sync your holdings to receive daily digests.</p>
                ) : (
                    <div className="news-list">
                        {news.map((n) => (
                            <div
                                key={n._id || n.url}
                                className={`news-item ${n.isRead ? '' : 'news-item--unread'}`}
                                onClick={() => !n.isRead && handleMarkRead(n._id)}
                            >
                                {n.instrumentSymbol && <span className="news-item-symbol">{n.instrumentSymbol}</span>}
                                <div className="news-item-body">
                                    <div className="news-item-title">
                                        <a href={n.url} target="_blank" rel="noopener noreferrer">{n.title}</a>
                                    </div>
                                    <div className="news-item-meta">{n.source} · {relativeTime(n.publishedAt)}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Earnings calendar */}
            {earnings.length > 0 && (
                <section className="portfolio-section">
                    <h3 className="portfolio-section-title">Upcoming Earnings</h3>
                    <div className="holdings-table-wrap">
                        <table className="holdings-table">
                            <thead>
                                <tr><th>Company</th><th>Date</th><th>Quarter</th></tr>
                            </thead>
                            <tbody>
                                {earnings.map((e, i) => (
                                    <tr key={i}>
                                        <td style={{ fontWeight: 600 }}>{e.name}</td>
                                        <td>{new Date(e.date).toLocaleDateString('en-IN')}</td>
                                        <td>{e.quarter || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Dividend events */}
            {dividends.length > 0 && (
                <section className="portfolio-section">
                    <h3 className="portfolio-section-title">Dividend Events</h3>
                    <div className="holdings-table-wrap">
                        <table className="holdings-table">
                            <thead>
                                <tr><th>Company</th><th>Ex-Date</th><th>Dividend/Share</th></tr>
                            </thead>
                            <tbody>
                                {dividends.map((d, i) => (
                                    <tr key={i}>
                                        <td style={{ fontWeight: 600 }}>{d.name}</td>
                                        <td>{new Date(d.exDate).toLocaleDateString('en-IN')}</td>
                                        <td>{fmtCurrency(d.dividendPerShare)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </>
    );
};

/* ═══════════════════════════════════════════════════════════════
   TAB 5: BROKER CONNECTIONS
   ═══════════════════════════════════════════════════════════════ */
const BROKER_DEFS = [
    {
        key: 'ICICI_BREEZE',
        name: 'ICICI Direct (Breeze)',
        logo: '🏦',
        fields: [
            { name: 'apiKey', label: 'App Key', type: 'text' },
            { name: 'apiSecret', label: 'App Secret', type: 'password' },
            { name: 'sessionToken', label: 'Session Token (daily)', type: 'password', help: 'Generated daily, valid until midnight' },
        ],
    },
    {
        key: 'GROWW',
        name: 'Groww',
        logo: '🌱',
        fields: [
            { name: 'accessCode', label: 'Access Code / Access Token', type: 'password', help: 'Groww access token resets daily (typically around 6 AM)' },
            { name: 'apiKey', label: 'API Key (optional)', type: 'text' },
            { name: 'refreshToken', label: 'Refresh Token (optional)', type: 'password' },
            { name: 'clientId', label: 'OAuth Client ID (optional)', type: 'text' },
            { name: 'clientSecret', label: 'OAuth Client Secret (optional)', type: 'password' },
        ],
    },
];

const BrokersTab = () => {
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [connectModal, setConnectModal] = useState(null); // broker key or null
    const [formValues, setFormValues] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [refreshModal, setRefreshModal] = useState(null);
    const [refreshToken, setRefreshToken] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listBrokerConnections();
            setConnections(res?.data ?? res ?? []);
        } catch { setConnections([]); }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const connMap = {};
    connections.forEach((c) => { connMap[c.broker] = c; });

    const [brokerError, setBrokerError] = useState('');

    const handleConnect = async () => {
        if (!connectModal) return;
        setSubmitting(true);
        setBrokerError('');
        try {
            await connectBroker(connectModal, formValues);
            setConnectModal(null);
            setFormValues({});
            load();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Connection failed';
            setBrokerError(msg);
        }
        setSubmitting(false);
    };

    const handleDisconnect = async (broker) => {
        if (!window.confirm(`Disconnect ${broker}? Stored credentials will be deleted.`)) return;
        try {
            await disconnectBroker(broker);
            load();
        } catch { /* ignore */ }
    };

    const handleRefreshSession = async () => {
        if (!refreshModal) return;
        setSubmitting(true);
        setBrokerError('');
        try {
            await refreshBrokerSession(refreshModal, { sessionToken: refreshToken });
            setRefreshModal(null);
            setRefreshToken('');
            load();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Session refresh failed';
            setBrokerError(msg);
        }
        setSubmitting(false);
    };

    if (loading) return <div className="portfolio-loading">Loading broker connections…</div>;

    return (
        <>
            <div className="broker-cards">
                {BROKER_DEFS.map((bd) => {
                    const conn = connMap[bd.key];
                    const isConnected = conn?.status === 'connected';
                    const needsRefresh = conn?.status === 'session_expired';

                    return (
                        <div key={bd.key} className="broker-card">
                            <div className="broker-card-header">
                                <div className="broker-card-logo">{bd.logo}</div>
                                <div>
                                    <div className="broker-card-name">{bd.name}</div>
                                    <div className="broker-card-status">
                                        <span className={`sync-status-dot sync-status-dot--${isConnected ? 'ok' : needsRefresh ? 'stale' : 'disconnected'}`} />
                                        {isConnected ? 'Connected' : needsRefresh ? 'Session Expired' : 'Not connected'}
                                    </div>
                                </div>
                            </div>

                            {conn && (
                                <div className="broker-card-meta">
                                    {conn.lastSyncAt && <span>Last sync: {relativeTime(conn.lastSyncAt)}</span>}
                                    {conn.lastError && <span style={{ color: 'var(--red)' }}>Error: {conn.lastError}</span>}
                                </div>
                            )}

                            <div className="broker-card-actions">
                                {!conn && (
                                    <button
                                        className="broker-action-btn broker-action-btn--primary"
                                        onClick={() => { setConnectModal(bd.key); setFormValues({}); }}
                                    >
                                        Connect
                                    </button>
                                )}
                                {needsRefresh && (
                                    <button
                                        className="broker-action-btn broker-action-btn--primary"
                                        onClick={() => { setRefreshModal(bd.key); setRefreshToken(''); }}
                                    >
                                        Refresh Session
                                    </button>
                                )}
                                {conn && (
                                    <>
                                        <button
                                            className="broker-action-btn broker-action-btn--secondary"
                                            onClick={() => triggerSync(bd.key).then(load)}
                                        >
                                            ⟳ Sync
                                        </button>
                                        <button
                                            className="broker-action-btn broker-action-btn--danger"
                                            onClick={() => handleDisconnect(bd.key)}
                                        >
                                            Disconnect
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Connect modal */}
            {connectModal && (
                <div className="broker-connect-modal-overlay" onClick={() => setConnectModal(null)}>
                    <div className="broker-connect-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Connect {BROKER_DEFS.find((b) => b.key === connectModal)?.name}</h3>
                        <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', margin: '0 0 0.5rem' }}>
                            Credentials are encrypted at rest and never exposed after saving.
                        </p>
                        {BROKER_DEFS.find((b) => b.key === connectModal)?.fields.map((f) => (
                            <React.Fragment key={f.name}>
                                <label>{f.label}{f.help && <span style={{ fontWeight: 400, marginLeft: 4, color: 'var(--muted)' }}>— {f.help}</span>}</label>
                                <input
                                    type={f.type}
                                    value={formValues[f.name] || ''}
                                    onChange={(e) => setFormValues((prev) => ({ ...prev, [f.name]: e.target.value }))}
                                    autoComplete="off"
                                />
                            </React.Fragment>
                        ))}
                        {brokerError && <p style={{ color: 'var(--red)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>{brokerError}</p>}
                        <div className="modal-actions">
                            <button className="broker-action-btn broker-action-btn--secondary" onClick={() => { setConnectModal(null); setBrokerError(''); }}>Cancel</button>
                            <button className="broker-action-btn broker-action-btn--primary" onClick={handleConnect} disabled={submitting}>
                                {submitting ? 'Connecting…' : 'Connect'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Refresh session modal (Breeze daily session key) */}
            {refreshModal && (
                <div className="broker-connect-modal-overlay" onClick={() => setRefreshModal(null)}>
                    <div className="broker-connect-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Refresh Session — {BROKER_DEFS.find((b) => b.key === refreshModal)?.name}</h3>
                        <p style={{ fontSize: '0.82rem', color: 'var(--text-2)', margin: '0 0 0.5rem' }}>
                            Your session token has expired. Please paste a new session token.
                        </p>
                        <label>Session Token</label>
                        <input
                            type="password"
                            value={refreshToken}
                            onChange={(e) => setRefreshToken(e.target.value)}
                            autoComplete="off"
                        />
                        {brokerError && <p style={{ color: 'var(--red)', fontSize: '0.85rem', margin: '0.5rem 0 0' }}>{brokerError}</p>}
                        <div className="modal-actions">
                            <button className="broker-action-btn broker-action-btn--secondary" onClick={() => { setRefreshModal(null); setBrokerError(''); }}>Cancel</button>
                            <button className="broker-action-btn broker-action-btn--primary" onClick={handleRefreshSession} disabled={submitting}>
                                {submitting ? 'Saving…' : 'Save Session'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PortfolioPage;
